'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef, type ReactNode } from 'react';

import { AccessibilityRuntime } from '@/components/accessibility/AccessibilityRuntime';
import { LocaleRuntime } from '@/components/i18n/LocaleRuntime';
import { ObservabilityRuntime } from '@/components/observability/ObservabilityRuntime';
import { PwaRuntime } from '@/components/pwa/PwaRuntime';
import { InitialCompactViewportProvider } from '@/components/responsive/InitialCompactViewportProvider';
import { RouteTopLoader } from '@/components/routing/RouteTopLoader';
import { ThemeRuntime } from '@/components/theme/ThemeRuntime';
import { AppRoute, isVpnPlusShareCodeRoutePathname } from '@/constants/routes';
import { RootRuntimeVariant } from '@/enums/RootRuntimeVariant';
import type { RuntimeI18nPayload } from '@/features/i18n/payload';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v16-appRouter';

import { requestFullRuntimeDocumentHandoff } from '../RootRuntimeBoundary/rootRuntimeDocumentHandoff';

type RootRuntimePathBoundaryProps = {
    children: ReactNode;
    initialIsCompact: boolean;
    initialPayload: RuntimeI18nPayload;
    variant: RootRuntimeVariant;
};

export const buildFullRuntimeHandoffHref = ({ pathname, search }: { pathname: string; search: string }) => `${pathname}${search}`;

const isPublicRuntimePathname = (pathname: string) => pathname === AppRoute.Login || isVpnPlusShareCodeRoutePathname(pathname);

const PublicRuntimeProviders = ({
    children,
    initialIsCompact,
    initialPayload,
}: Pick<RootRuntimePathBoundaryProps, 'children' | 'initialIsCompact' | 'initialPayload'>) => (
    <AppRouterCacheProvider options={{ enableCssLayer: true }}>
        <LocaleRuntime initialPayload={initialPayload}>
            <ThemeRuntime>
                <AccessibilityRuntime>
                    <InitialCompactViewportProvider initialIsCompact={initialIsCompact}>{children}</InitialCompactViewportProvider>
                </AccessibilityRuntime>
            </ThemeRuntime>
        </LocaleRuntime>
    </AppRouterCacheProvider>
);

export const RootRuntimePathBoundary = ({ children, initialIsCompact, initialPayload, variant }: RootRuntimePathBoundaryProps) => {
    const pathname = usePathname();
    const usesPublicRuntime =
        pathname === null
            ? variant === RootRuntimeVariant.PublicLogin || variant === RootRuntimeVariant.PublicShare
            : isPublicRuntimePathname(pathname);
    const requiresFullRuntimeDocumentHandoff = pathname !== null && !usesPublicRuntime && variant !== RootRuntimeVariant.Full;
    const hasStartedFullRuntimeDocumentHandoff = useRef(false);

    useEffect(() => {
        if (!requiresFullRuntimeDocumentHandoff || !pathname || hasStartedFullRuntimeDocumentHandoff.current) {
            return;
        }

        hasStartedFullRuntimeDocumentHandoff.current = true;
        requestFullRuntimeDocumentHandoff(
            buildFullRuntimeHandoffHref({
                pathname,
                search: window.location.search,
            })
        );
    }, [pathname, requiresFullRuntimeDocumentHandoff]);

    if (requiresFullRuntimeDocumentHandoff) {
        return null;
    }

    if (usesPublicRuntime) {
        return (
            <PublicRuntimeProviders initialIsCompact={initialIsCompact} initialPayload={initialPayload}>
                {children}
            </PublicRuntimeProviders>
        );
    }

    return (
        <>
            <RouteTopLoader />
            <AppRouterCacheProvider options={{ enableCssLayer: true }}>
                <ObservabilityRuntime>
                    <LocaleRuntime initialPayload={initialPayload}>
                        <ThemeRuntime>
                            <AccessibilityRuntime>
                                <PwaRuntime />
                                <InitialCompactViewportProvider initialIsCompact={initialIsCompact}>{children}</InitialCompactViewportProvider>
                            </AccessibilityRuntime>
                        </ThemeRuntime>
                    </LocaleRuntime>
                </ObservabilityRuntime>
            </AppRouterCacheProvider>
        </>
    );
};
